import api from "@/lib/api";
import { clientId as locationClientId } from "@/lib/location";

const SUB_KEY = "em_push_last_endpoint";
const DECISION_KEY = "em_push_decision"; // 'granted' | 'denied' | 'later' | 'unsupported'
const LAST_PROMPT_KEY = "em_push_prompted_at";

export const pushSupport = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// True when we're on iOS Safari and the site is NOT installed to home screen.
// Web Push on iOS requires the standalone PWA — attempting it from Safari
// silently fails, so we detect and message it clearly.
export const isIOSNonStandalone = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIDevice = /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && "ontouchend" in document);
  if (!isIDevice) return false;
  const standalone = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
    || (window.navigator && window.navigator.standalone === true);
  return !standalone;
};

export const getDecision = () => localStorage.getItem(DECISION_KEY);
export const setDecision = (v) => {
  localStorage.setItem(DECISION_KEY, v);
  localStorage.setItem(LAST_PROMPT_KEY, String(Date.now()));
};

/** Should we show the opt-in modal right now? */
export const shouldPrompt = () => {
  if (!pushSupport()) return false;
  if (Notification.permission === "granted") return false;
  if (Notification.permission === "denied") return false;
  const decision = getDecision();
  if (decision === "granted" || decision === "denied") return false;
  const last = Number(localStorage.getItem(LAST_PROMPT_KEY) || 0);
  // Re-prompt max once every 5 days if user chose 'later'.
  if (decision === "later" && Date.now() - last < 5 * 24 * 60 * 60 * 1000) return false;
  return true;
};

/** True once the guest has resolved the notification step (either way). */
export const isPushStepSettled = () => {
  if (!pushSupport()) return true;
  if (Notification.permission === "granted" || Notification.permission === "denied") return true;
  const decision = getDecision();
  return decision === "granted" || decision === "denied" || decision === "later";
};

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration("/service-worker.js");
    if (existing) return existing;
    return await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  } catch (e) {
    console.warn("[push] SW register failed", e);
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) output[i] = rawData.charCodeAt(i);
  return output;
}

function _readGuestName() {
  try { return localStorage.getItem("em_guest_name") || null; } catch { return null; }
}
function _tzOffsetMin() {
  // JS getTimezoneOffset returns minutes to add to local to get UTC.
  // We invert it for the more common "minutes local is ahead of UTC" (e.g. CET=+60).
  try { return -new Date().getTimezoneOffset(); } catch { return 0; }
}

export async function subscribeCurrent(lang = "en") {
  if (!pushSupport()) {
    const err = new Error("unsupported");
    err.kind = "unsupported";
    throw err;
  }
  if (isIOSNonStandalone()) {
    // iOS only allows Web Push from an installed PWA.
    const err = new Error("ios-install-required");
    err.kind = "ios-install-required";
    throw err;
  }
  await ensureServiceWorker();
  // Ensure the SW is actually active — subscribe() can fail if it isn't yet.
  const reg = await navigator.serviceWorker.ready;
  if (!reg) {
    const err = new Error("sw-failed");
    err.kind = "sw-failed";
    throw err;
  }

  const perm = await Notification.requestPermission();
  if (perm === "denied") {
    setDecision("denied");
    return { ok: false, permission: "denied" };
  }
  if (perm !== "granted") {
    setDecision("later");
    return { ok: false, permission: perm };
  }

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      const { data } = await api.get("/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
    } catch (e) {
      // Permission was granted but the subscription failed (network / VAPID / OS).
      const wrapped = new Error("subscribe-failed");
      wrapped.kind = "subscribe-failed";
      wrapped.cause = e;
      throw wrapped;
    }
  }

  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    lang,
    user_agent: navigator.userAgent,
    client_id: locationClientId(),
    tz_offset_min: _tzOffsetMin(),
    guest_name: _readGuestName(),
  });
  localStorage.setItem(SUB_KEY, json.endpoint);
  try { localStorage.setItem("em_last_push_endpoint", json.endpoint); } catch { /* ignore */ }
  setDecision("granted");
  return { ok: true, permission: "granted" };
}

export async function snoozePush(days = 7) {
  const endpoint = _lastEndpoint();
  if (!endpoint) return { ok: false, reason: "no-endpoint" };
  try {
    await api.post("/push/snooze", { endpoint, days });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "api", error: e };
  }
}

export async function unsnoozePush() {
  const endpoint = _lastEndpoint();
  if (!endpoint) return { ok: false, reason: "no-endpoint" };
  try {
    await api.post("/push/unsnooze", { endpoint, days: 1 });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "api", error: e };
  }
}

export async function fetchPushStatus() {
  const endpoint = _lastEndpoint();
  if (!endpoint) return null;
  try {
    const { data } = await api.get(`/push/status?endpoint=${encodeURIComponent(endpoint)}`);
    return data || null;
  } catch { return null; }
}

function _lastEndpoint() {
  try { return localStorage.getItem("em_last_push_endpoint") || localStorage.getItem(SUB_KEY); } catch { return null; }
}

export async function unsubscribeCurrent() {
  const reg = await ensureServiceWorker();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
  localStorage.removeItem(SUB_KEY);
  return { ok: true };
}

/**
 * Silently subscribe when permission is already granted (e.g. the guest
 * enabled it in Chrome site-settings after dismissing our modal).
 * Called from the app shell on every load — safe to call repeatedly.
 */
export async function autoSubscribeIfReady(lang = "en") {
  if (!pushSupport()) return { ok: false, reason: "unsupported" };
  if (isIOSNonStandalone()) return { ok: false, reason: "ios-install-required" };
  if (Notification.permission !== "granted") return { ok: false, reason: "not-granted" };
  // Already have a local endpoint? Refresh last_seen once but no subscribe cycle.
  const localEndpoint = localStorage.getItem(SUB_KEY);
  try {
    await ensureServiceWorker();
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get("/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
    }
    const json = sub.toJSON();
    if (json.endpoint === localEndpoint) return { ok: true, cached: true };
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      lang,
      user_agent: navigator.userAgent,
      client_id: locationClientId(),
      tz_offset_min: _tzOffsetMin(),
      guest_name: _readGuestName(),
    });
    try { localStorage.setItem("em_last_push_endpoint", json.endpoint); } catch { /* ignore */ }
    localStorage.setItem(SUB_KEY, json.endpoint);
    setDecision("granted");
    return { ok: true, cached: false };
  } catch (e) {
    console.warn("[push] auto-subscribe failed", e);
    return { ok: false, reason: "error", error: e };
  }
}

/** True when the browser has already blocked notifications (need site-setting fix). */
export function isPreDenied() {
  return pushSupport() && Notification.permission === "denied";
}
